import { z } from 'zod';
import { InquiryStatus } from '../enums';
import { LIMITS } from '../constants';
import { emailSchema, paginationQuerySchema } from './common';
/**
 * Public contact form ("Let's talk scale." in the reference design).
 *
 * This is the only unauthenticated write endpoint in the application, so it
 * carries three independent defences: a strict schema, a hard rate limit, and
 * the honeypot below. None is sufficient alone.
 */
export const createInquirySchema = z.object({
    /**
     * One field rather than first and last.
     *
     * A split name asks the visitor to model their name the way a Western HR system
     * does, which is wrong for a good share of the markets this firm hires in — mononyms,
     * patronymics, and family-name-first orders all survive a single field and none of
     * them survive two. 160 is the two old 80s put together, so no historical row was
     * truncated by the merge.
     */
    fullName: z.string().trim().min(1, 'Enter your full name.').max(160),
    email: emailSchema,
    phone: z.string().trim().max(40).optional(),
    message: z
        .string()
        .trim()
        .min(LIMITS.inquiryMessage.min, 'Tell us a little about what you need.')
        .max(LIMITS.inquiryMessage.max),
    /**
     * Honeypot. Rendered off-screen and left empty by humans; naive bots fill
     * every field they find. A filled value is accepted with a 201 and then
     * discarded, so the bot sees success and does not retry with a new strategy.
     */
    website: z.string().max(200).optional(),
    /**
     * Milliseconds between form render and submit, sent by the client. Anything
     * under ~1.5s is mechanical. Advisory only — it downgrades a submission to
     * SPAM for review rather than rejecting it, because a fast typist on a
     * prefilled form is possible and losing a real lead is worse than triaging one.
     */
    elapsedMs: z.coerce.number().int().min(0).optional(),
    /** Which page the form was submitted from, for attribution. */
    sourcePath: z.string().trim().max(200).optional(),
});
export const updateInquirySchema = z.object({
    status: z.enum(InquiryStatus),
    /** Internal notes. Never surfaced publicly. */
    notes: z.string().trim().max(4000).nullish(),
});
export const inquiryQuerySchema = paginationQuerySchema.extend({
    /**
     * Free text, matched against the name, email, phone number and message body.
     *
     * Email alone was the original behaviour and it was the wrong single field to pick: the
     * thing somebody remembers about an enquiry a week later is the person's name or what
     * they asked for, not the address they typed it from.
     */
    q: z.string().trim().max(120).optional(),
    status: z.enum(InquiryStatus).optional(),
});
/**
 * How many unread enquiries the bell lists.
 *
 * Shared so the client can say "and N more" honestly rather than guessing whether the
 * list it received was truncated.
 */
export const INQUIRY_NOTIFICATION_LIMIT = 8;
/** How much of the message the notification list carries. */
export const INQUIRY_EXCERPT_LENGTH = 140;
/** Shortest plausible human fill time, in milliseconds. */
export const MIN_HUMAN_FILL_MS = 1_500;
//# sourceMappingURL=inquiry.js.map